import React, { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ArrowLeft, Save, Trash2, Plus, X } from "lucide-react";
import { toast } from "sonner";

const NUMERIC = new Set([
  "land_area_sqft", "construction_area_sqft", "fsi",
  "number_of_buildings", "number_of_towers", "number_of_wings", "total_floors",
  "number_of_flats", "number_of_row_houses", "number_of_shops",
  "number_of_offices", "number_of_showrooms",
  "estimated_cost", "estimated_revenue", "estimated_profit",
]);

export default function ProjectEdit() {
  const { id } = useParams();
  const nav = useNavigate();
  const qc = useQueryClient();
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);
  const [reason, setReason] = useState("");
  const [imgInput, setImgInput] = useState("");

  const { data: project, isLoading } = useQuery({
    queryKey: ["project", id],
    queryFn: async () => (await api.get(`/projects/${id}`)).data,
  });

  useEffect(() => {
    if (project) {
      setForm({
        name: project.name || "",
        code: project.code || "",
        location: project.location || "",
        google_maps: project.google_maps || "",
        project_type: project.project_type || "residential",
        ownership_type: project.ownership_type || "owned",
        rera_number: project.rera_number || "",
        status: project.status || "active",
        land_area_sqft: project.land_area_sqft || 0,
        construction_area_sqft: project.construction_area_sqft || 0,
        fsi: project.fsi || 0,
        number_of_buildings: project.number_of_buildings || 0,
        number_of_towers: project.number_of_towers || 0,
        number_of_wings: project.number_of_wings || 0,
        total_floors: project.total_floors || 0,
        number_of_flats: project.number_of_flats || 0,
        number_of_row_houses: project.number_of_row_houses || 0,
        number_of_shops: project.number_of_shops || 0,
        number_of_offices: project.number_of_offices || 0,
        number_of_showrooms: project.number_of_showrooms || 0,
        unit_configuration: project.unit_configuration || "",
        building_config: project.building_config || "",
        estimated_cost: project.estimated_cost || 0,
        estimated_revenue: project.estimated_revenue || 0,
        estimated_profit: project.estimated_profit || 0,
        start_date: (project.start_date || "").slice(0, 10),
        completion_date: (project.completion_date || "").slice(0, 10),
        project_manager: project.project_manager || "",
        architect: project.architect || "",
        structural_consultant: project.structural_consultant || "",
        legal_consultant: project.legal_consultant || "",
        other_consultants: project.other_consultants || "",
        thumbnail_url: project.thumbnail_url || "",
        images: project.images || [],
        description: project.description || "",
      });
    }
  }, [project]);

  if (isLoading || !form) return <div className="text-sm text-zinc-500">Loading project…</div>;

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  function textField(k, label, opts = {}) {
    const type = opts.type || "text";
    return (
      <div>
        <Label htmlFor={k} className="text-[11px] uppercase tracking-widest text-zinc-500">{label}</Label>
        <Input
          id={k}
          type={type}
          value={form[k] ?? ""}
          onChange={(e) => set(k, e.target.value)}
          className="mt-1 h-10 border-zinc-300"
          data-testid={`edit-${k}`}
        />
      </div>
    );
  }

  async function save() {
    setBusy(true);
    try {
      const payload = { ...form };
      for (const k of NUMERIC) payload[k] = Number(payload[k]) || 0;
      if (reason) payload._reason = reason;
      await api.patch(`/projects/${id}`, payload);
      toast.success("Project updated");
      qc.invalidateQueries({ queryKey: ["project", id] });
      qc.invalidateQueries({ queryKey: ["projects"] });
      nav(`/projects/${id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
    } finally { setBusy(false); }
  }

  async function remove() {
    if (!window.confirm("Delete this project and ALL its units + land-owners? This cannot be undone.")) return;
    try {
      await api.delete(`/projects/${id}`);
      toast.success("Project deleted");
      nav("/projects");
    } catch { toast.error("Delete failed"); }
  }

  function addImage() {
    if (!imgInput.trim()) return;
    set("images", [...(form.images || []), imgInput.trim()]);
    setImgInput("");
  }
  function removeImage(i) {
    set("images", form.images.filter((_, idx) => idx !== i));
  }

  return (
    <div className="space-y-6 max-w-5xl" data-testid="project-edit-page">
      <Link to={`/projects/${id}`} className="inline-flex items-center gap-1 text-xs text-zinc-500 hover:text-zinc-800">
        <ArrowLeft size={12} /> Back to project
      </Link>
      <div className="flex items-end justify-between">
        <div>
          <div className="text-[11px] uppercase tracking-[0.15em] font-semibold text-emerald-700">Edit Site</div>
          <h1 className="font-heading text-3xl font-bold mt-1">{project.name}</h1>
          <p className="text-sm text-zinc-500 mt-1">All changes are versioned in the audit trail with your name and timestamp.</p>
        </div>
      </div>

      <Card className="bg-white border-zinc-200">
        <CardHeader><CardTitle className="font-heading text-base">Basic Details</CardTitle></CardHeader>
        <CardContent className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {textField("name", "Project Name")}
          {textField("code", "Project Code")}
          {textField("location", "Location")}
          {textField("google_maps", "Google Maps URL")}
          {textField("rera_number", "RERA Number")}
          <div>
            <Label className="text-[11px] uppercase tracking-widest text-zinc-500">Project Type</Label>
            <Select value={form.project_type} onValueChange={(v) => set("project_type", v)}>
              <SelectTrigger className="mt-1 h-10" data-testid="edit-project_type"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="residential">Residential</SelectItem>
                <SelectItem value="row_house">Row House</SelectItem>
                <SelectItem value="commercial">Commercial</SelectItem>
                <SelectItem value="mixed">Mixed-use</SelectItem>
                <SelectItem value="land_development">Land Development</SelectItem>
                <SelectItem value="joint_development">Joint Development</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[11px] uppercase tracking-widest text-zinc-500">Ownership Type</Label>
            <Select value={form.ownership_type} onValueChange={(v) => set("ownership_type", v)}>
              <SelectTrigger className="mt-1 h-10" data-testid="edit-ownership_type"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="owned">Company Owned</SelectItem>
                <SelectItem value="jda">Joint Development (JDA)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[11px] uppercase tracking-widest text-zinc-500">Project Status</Label>
            <Select value={form.status} onValueChange={(v) => set("status", v)}>
              <SelectTrigger className="mt-1 h-10" data-testid="edit-status"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="planning">Planning</SelectItem>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="on_hold">On Hold</SelectItem>
                <SelectItem value="completed">Completed</SelectItem>
                <SelectItem value="handed_over">Handed Over</SelectItem>
                <SelectItem value="cancelled">Cancelled</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      <Card className="bg-white border-zinc-200">
        <CardHeader><CardTitle className="font-heading text-base">Sizing</CardTitle></CardHeader>
        <CardContent className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {textField("land_area_sqft", "Land Area (sqft)", { type: "number" })}
          {textField("construction_area_sqft", "Construction Area (sqft)", { type: "number" })}
          {textField("fsi", "FSI", { type: "number" })}
        </CardContent>
      </Card>

      <Card className="bg-white border-zinc-200">
        <CardHeader>
          <CardTitle className="font-heading text-base">Project Configuration</CardTitle>
          <p className="text-[11px] text-zinc-500 mt-1">
            Structure counts — the Interactive Sales Board auto-scales its tower/floor grid to match.
          </p>
        </CardHeader>
        <CardContent className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {textField("number_of_buildings", "Buildings", { type: "number" })}
          {textField("number_of_towers", "Towers", { type: "number" })}
          {textField("number_of_wings", "Wings", { type: "number" })}
          {textField("total_floors", "Total Floors (per tower)", { type: "number" })}
          {textField("number_of_flats", "Flats", { type: "number" })}
          {textField("number_of_row_houses", "Row Houses", { type: "number" })}
          {textField("number_of_shops", "Shops", { type: "number" })}
          {textField("number_of_offices", "Offices", { type: "number" })}
          {textField("number_of_showrooms", "Showrooms", { type: "number" })}
          <div className="col-span-2 md:col-span-3">
            {textField("unit_configuration", "Unit Configuration (e.g., 2 & 3 BHK, 1050-1450 sqft)")}
          </div>
          <div className="col-span-2 md:col-span-3 lg:col-span-4">
            {textField("building_config", "Building Configuration Notes")}
          </div>
        </CardContent>
      </Card>

      <Card className="bg-white border-zinc-200">
        <CardHeader><CardTitle className="font-heading text-base">Financials</CardTitle></CardHeader>
        <CardContent className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {textField("estimated_cost", "Estimated Cost (₹)", { type: "number" })}
          {textField("estimated_revenue", "Estimated Revenue (₹)", { type: "number" })}
          {textField("estimated_profit", "Estimated Profit (₹)", { type: "number" })}
        </CardContent>
      </Card>

      <Card className="bg-white border-zinc-200">
        <CardHeader><CardTitle className="font-heading text-base">Timeline & Team</CardTitle></CardHeader>
        <CardContent className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {textField("start_date", "Start Date", { type: "date" })}
          {textField("completion_date", "Completion Date", { type: "date" })}
          {textField("project_manager", "Project Manager")}
          {textField("architect", "Architect")}
          {textField("structural_consultant", "Structural Consultant")}
          {textField("legal_consultant", "Legal Consultant")}
          <div className="md:col-span-3">{textField("other_consultants", "Other Consultants")}</div>
        </CardContent>
      </Card>

      <Card className="bg-white border-zinc-200">
        <CardHeader><CardTitle className="font-heading text-base">Description & Media</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          {textField("thumbnail_url", "Thumbnail URL")}
          <div>
            <Label className="text-[11px] uppercase tracking-widest text-zinc-500">Description</Label>
            <Textarea
              rows={4}
              value={form.description}
              onChange={(e) => set("description", e.target.value)}
              className="mt-1 border-zinc-300"
              data-testid="edit-description"
            />
          </div>
          <div>
            <Label className="text-[11px] uppercase tracking-widest text-zinc-500">Project Images (URLs)</Label>
            <div className="flex gap-2 mt-1">
              <Input
                placeholder="https://…"
                value={imgInput}
                onChange={(e) => setImgInput(e.target.value)}
                className="border-zinc-300"
                data-testid="edit-image-input"
              />
              <Button type="button" variant="outline" onClick={addImage} data-testid="edit-image-add">
                <Plus size={14} className="mr-1" /> Add
              </Button>
            </div>
            {form.images?.length > 0 && (
              <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-3">
                {form.images.map((src, i) => (
                  <div key={`${src}-${i}`} className="relative group border border-zinc-200 rounded overflow-hidden">
                    <img src={src} alt="" className="w-full h-24 object-cover" />
                    <button
                      type="button"
                      onClick={() => removeImage(i)}
                      className="absolute top-1 right-1 bg-black/70 text-white p-1 rounded opacity-0 group-hover:opacity-100 transition-opacity"
                      data-testid={`edit-image-remove-${i}`}
                    >
                      <X size={12} />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      <Card className="bg-white border-zinc-200">
        <CardHeader><CardTitle className="font-heading text-base">Audit</CardTitle></CardHeader>
        <CardContent>
          <Label className="text-[11px] uppercase tracking-widest text-zinc-500">Reason for change (optional)</Label>
          <Input
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="e.g., Revised RERA approval received; new completion date."
            className="mt-1 h-10 border-zinc-300"
            data-testid="edit-reason"
          />
        </CardContent>
      </Card>

      <div className="flex gap-3 justify-between">
        <Button type="button" variant="outline" onClick={remove} className="text-red-600 border-red-200 hover:bg-red-50" data-testid="delete-project-btn">
          <Trash2 size={14} className="mr-1" /> Delete Project
        </Button>
        <div className="flex gap-3">
          <Button type="button" variant="outline" onClick={() => nav(`/projects/${id}`)} data-testid="cancel-edit-btn">Cancel</Button>
          <Button type="button" disabled={busy} onClick={save} className="bg-emerald-700 hover:bg-emerald-800" data-testid="save-edit-btn">
            <Save size={14} className="mr-1" /> {busy ? "Saving…" : "Save Changes"}
          </Button>
        </div>
      </div>
    </div>
  );
}
